import { useFavorites } from '@/lib/hooks/use-favorites';
import AudioPlayer from '@/components/audio/audio-player';
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import AudioCard from '@/components/audio-card';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';

import { useProfile } from '@/lib/providers';
import {
  AdminRole,
  Content,
  ContentConfig,
  ContentType,
  ContentWithConfig,
} from '@/types/custom';
import { useContentList } from '@/lib/hooks/use-content';
import MeditationEdit from '@/components/meditation/meditation-edit';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { searchContentList } from '@/lib/search';
import EditableDescription from '@/components/editable-description';

interface AudioCatalogProps {
  contentType: ContentType;
  contentNickname: string;
  contentDescription?: string;
  contentIcon: any; // TODO react.ForwardRefExoticComponent;
  pageKey?: string;
}

/**
 * Renders a page with a grid of playable audio cards.
 * (All of a given type e.g. "nature_sound").
 */
export default function AudioCatalog({
  contentType,
  contentNickname,
  contentDescription,
  contentIcon,
  pageKey,
}: AudioCatalogProps) {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;
  const { favorites, toggleFavorite } = useFavorites();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContent, setFilteredContent] = useState<ContentWithConfig[]>(
    [],
  );

  // Fetch nature sounds from database with their configs
  const { contentWithConfigs, loading: dbLoading } = useContentList(
    contentType,
    [],
  );
  const [currentContent, setCurrentContent] =
    useState<ContentWithConfig | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | undefined>(
    undefined,
  );

  // Track if a card was clicked to play audio
  const [shouldPlay, setShouldPlay] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const { toast } = useToast();

  // Get the actief parameter from URL
  const actiefId = searchParams.get('actief');

  // Filter content based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredContent(contentWithConfigs);
      return;
    }

    const results = searchContentList(
      searchTerm,
      contentWithConfigs.map((x) => x.content),
    );

    const filtered = contentWithConfigs.filter((item, i) => results[i]);
    setFilteredContent(filtered);
  }, [searchTerm, contentWithConfigs]);

  // Set the first content as current when data loads, or the one specified in URL
  useEffect(() => {
    if (contentWithConfigs.length > 0 && !currentContent) {
      let targetContent: ContentWithConfig | null = null;

      // If there's an actief parameter, try to find that content
      if (actiefId) {
        targetContent =
          contentWithConfigs.find((item) => item.content.id === actiefId) ||
          null;
        if (targetContent) {
          setShouldPlay(true);
        } else {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('actief');
          setSearchParams(newSearchParams);
        }
      }

      // If no actief content found or no actief parameter, use the first content
      if (!targetContent) {
        targetContent = contentWithConfigs[0];
      }

      // Use the first config from the configs array
      if (targetContent && targetContent.configs.length > 0) {
        setCurrentContent(targetContent);
      }
    }
  }, [contentWithConfigs, currentContent, actiefId]);

  // Update URL when currentContent changes
  useEffect(() => {
    if (currentContent) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('actief', currentContent.content.id);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [currentContent, setSearchParams, searchParams]);

  // Show toast when content has no audio available
  useEffect(() => {
    if (
      currentContent &&
      !(currentContent?.configs.at(0)?.audio_link || '').trim()
    ) {
      toast({
        title: 'Geen audio beschikbaar',
        description: `Er is geen audio beschikbaar voor "${currentContent?.content.title}".`,
        variant: 'default',
      });
      setShouldPlay(false);
    }
  }, [currentContent, toast]);

  const handleSave = () => {
    // Refresh the content list
    window.location.reload();
  };

  /**
   * Begin editing a given audio item, or create a new one if undefined.
   */
  const requestEdit = (
    content: Content | undefined,
    config: ContentConfig | undefined,
  ) => {
    setEditingContent(content);
    setShowEditDialog(true);
  };

  return (
    <div className="min-h-screen pb-32 relative overflow-hidden bg-background">
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-start">
              <div className="flex items-center gap-4 min-w-0">
                <h1 className="text-2xl font-semibold">{contentNickname}</h1>
              </div>

              {isAdmin && (
              <div
                className="relative"
                style={{ marginTop: '12px', marginLeft: '24px' }}
              >
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => requestEdit(undefined, undefined)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Toevoegen
                </Button>
              </div>
            )}
            </div>
            {contentDescription && pageKey ? (
              <EditableDescription pageKey={pageKey} defaultText={contentDescription} />
            ) : contentDescription ? (
              <p className="text-muted-foreground">{contentDescription}</p>
            ) : null}
          </div>

          {/* Search bar */}
          <div className="mb-8">
            <div className="relative w-full max-w-md">
              <div className="flex items-center h-12 rounded-full border border-input bg-background px-4 py-2">
                <Search className="h-5 w-5 text-muted-foreground mr-2" />
                <input
                  type="search"
                  placeholder={`Zoek ${contentNickname.toLowerCase()}...`}
                  className="flex h-full w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Show search results count */}
        {searchTerm.trim() !== '' && (
          <div className="text-center py-4 text-muted-foreground">
            {filteredContent.length === 0 ? (
              <p>Geen resultaten gevonden voor "{searchTerm}"</p>
            ) : (
              <p>
                {filteredContent.length} resultaat
                {filteredContent.length !== 1 ? 'en' : ''} gevonden voor "
                {searchTerm}"
              </p>
            )}
          </div>
        )}

      {dbLoading ? (
        <div className="text-center py-8">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 xl:gap-5">
          {filteredContent.map((contentWithConfig) => {
            // Filter out unpublished content for non-admins
            if (!isAdmin && !contentWithConfig.content.published) {
              return null;
            }

            // For non-admins, skip content without configs
            // if (!isAdminOrDev && contentWithConfig.configs.length === 0) {
            //   return null;
            // }

            // Use the first config from the configs array, or null if no configs
            const config = contentWithConfig.configs.at(0);
            const isFavorite = favorites[contentWithConfig.content.id];

            return (
              <AudioCard
                key={contentWithConfig.content.id}
                content={contentWithConfig.content}
                config={config}
                isFavorite={isFavorite}
                onClick={() => {
                  // if (config) setCurrentContent(contentWithConfig);
                  setCurrentContent(contentWithConfig);
                  // Set flag to play audio after card click
                  setShouldPlay(true);
                }}
                onFavoriteToggle={(e) => {
                  e.stopPropagation();
                  toggleFavorite(contentWithConfig.content.id);
                }}
                requestEdit={(e, content, config) => {
                  e.stopPropagation();
                  requestEdit(content, config);
                }}
              />
            );
          })}
        </div>
      )}
      </div>

      {currentContent && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-50">
          <AudioPlayer
            id={currentContent.content.id}
            type={contentType === 'nature_sound' ? 'natuurgeluid' : contentType === 'music' ? 'music' : undefined}
            title={currentContent.content.title}
            subtitle={currentContent.content.description}
            imageUrl={currentContent.content.image_url || ''}
            audioUrl={currentContent.configs.at(0)?.audio_link || undefined}
            shouldPlay={shouldPlay}
            onPlayStarted={() => setShouldPlay(false)}
            shuffle={shuffle}
            onShuffleChange={setShuffle}
            className="w-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg"
            onNext={() => {
              if (shuffle && filteredContent.length > 1) {
                const currentIndex = filteredContent.findIndex(
                  (item) => item.content.id === currentContent.content.id,
                );
                let randomIndex: number;
                do {
                  randomIndex = Math.floor(Math.random() * filteredContent.length);
                } while (randomIndex === currentIndex);
                setCurrentContent(filteredContent[randomIndex]);
              } else {
                const currentIndex = filteredContent.findIndex(
                  (item) => item.content.id === currentContent.content.id,
                );
                const nextIndex = (currentIndex + 1) % filteredContent.length;
                setCurrentContent(filteredContent[nextIndex]);
              }
              setShouldPlay(true);
            }}
            onPrevious={() => {
              if (shuffle && filteredContent.length > 1) {
                const currentIndex = filteredContent.findIndex(
                  (item) => item.content.id === currentContent.content.id,
                );
                let randomIndex: number;
                do {
                  randomIndex = Math.floor(Math.random() * filteredContent.length);
                } while (randomIndex === currentIndex);
                setCurrentContent(filteredContent[randomIndex]);
              } else {
                const currentIndex = filteredContent.findIndex(
                  (item) => item.content.id === currentContent.content.id,
                );
                const prevIndex =
                  (currentIndex - 1 + filteredContent.length) %
                  filteredContent.length;
                setCurrentContent(filteredContent[prevIndex]);
              }
              setShouldPlay(true);
            }}
          />
        </div>
      )}

      {/* MeditationEdit Dialog */}
      <MeditationEdit
        content={editingContent}
        onSave={handleSave}
        isOpen={showEditDialog}
        handleClose={() => {
          setShowEditDialog(false);
          setEditingContent(undefined);
        }}
        defaultMeditationType={contentType}
      />
    </div>
  );
}
